/** Normalizers for Agent configurations APIs. */

import { parseAwsJson } from '@/utils/graphql'

function pickString(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return ''
  for (const key of keys) {
    const value = raw[key]
    if (value != null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

function pickArray(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return []
  for (const key of keys) {
    const value = raw[key]
    if (Array.isArray(value)) return value
  }
  return []
}

function pickBool(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return false
  for (const key of keys) {
    const value = raw[key]
    if (typeof value === 'boolean') return value
    if (value === 'true' || value === '1') return true
    if (value === 'false' || value === '0') return false
  }
  return false
}

/** Schema: informationInput */
export function normalizeInformationInputItem(raw, index = 0) {
  const sequence =
    raw?.item_sequence != null && !Number.isNaN(Number(raw.item_sequence))
      ? Number(raw.item_sequence)
      : index + 1

  const topicTypeRaw = pickString(
    raw,
    'topic_type',
    'topicType',
    'conversation_block_type',
    'conversationBlockType',
  )
  const hasQuestionFields =
    pickString(raw, 'parameter_name', 'parameterName') ||
    pickBool(raw, 'required', 'is_required', 'isRequired')

  return {
    item_sequence: sequence,
    topic_type: normalizeConversationBlockType(
      topicTypeRaw || (hasQuestionFields ? 'QUESTION' : 'INFORMATION'),
    ),
    parameter_name: pickString(
      raw,
      'parameter_name',
      'parameterName',
      'info_title',
      'infoTitle',
      'title',
    ),
    required: pickBool(raw, 'required', 'is_required', 'isRequired'),
    item_text: pickString(
      raw,
      'item_text',
      'itemText',
      'info_content',
      'infoContent',
      'content',
      'question',
      'text',
    ),
  }
}

export function emptyInformationInputRow(sequence = 1, topicType = 'INFORMATION') {
  return {
    item_sequence: sequence,
    topic_type: normalizeConversationBlockType(topicType),
    parameter_name: '',
    required: false,
    item_text: '',
  }
}

export function resequenceInformationItems(items) {
  return (Array.isArray(items) ? items : []).map((row, index) => ({
    ...normalizeInformationInputItem(row, index),
    item_sequence: index + 1,
  }))
}

export function serializeInformationItemsForApi(items, options = {}) {
  const { includeParameterName = true } = options
  return resequenceInformationItems(items)
    .filter((row) =>
      includeParameterName
        ? row.parameter_name?.trim() || row.item_text?.trim()
        : row.item_text?.trim(),
    )
    .map((row) => {
      const payload = {
        item_sequence: row.item_sequence,
        required: !!row.required,
        item_text: row.item_text.trim(),
      }
      if (includeParameterName && row.parameter_name?.trim()) {
        payload.parameter_name = row.parameter_name.trim()
      }
      return payload
    })
}

export const CONVERSATION_BLOCK_TYPE_OPTIONS = [
  { label: 'Information', value: 'INFORMATION' },
  { label: 'Question', value: 'QUESTION' },
]

export function normalizeConversationBlockType(value) {
  const type = String(value ?? '').trim().toUpperCase()
  return type === 'QUESTION' ? 'QUESTION' : 'INFORMATION'
}

export function isQuestionBlockType(blockType) {
  return normalizeConversationBlockType(blockType) === 'QUESTION'
}

export function formatConversationBlockTypeLabel(blockType) {
  return isQuestionBlockType(blockType) ? 'Question' : 'Information'
}

export function serializeRuleSetItemsForApi(items) {
  return resequenceInformationItems(items)
    .filter((row) => {
      const topicType = normalizeConversationBlockType(row.topic_type)
      return isQuestionBlockType(topicType)
        ? row.parameter_name?.trim() || row.item_text?.trim()
        : row.item_text?.trim()
    })
    .map((row) => {
      const topicType = normalizeConversationBlockType(row.topic_type)
      const payload = {
        item_sequence: row.item_sequence,
        topic_type: topicType,
        item_text: row.item_text.trim(),
      }
      if (isQuestionBlockType(topicType)) {
        payload.required = !!row.required
        if (row.parameter_name?.trim()) {
          payload.parameter_name = row.parameter_name.trim()
        }
      }
      return payload
    })
}

export function buildRuleSetMutationInput(ruleSet, actionIds, sessionContext) {
  const items =
    ruleSet.items ||
    ruleSet.rule_set_items ||
    ruleSet.information ||
    ruleSet.questions ||
    []
  return {
    org_id: sessionContext.org_id,
    rule_set_id: ruleSet.rule_set_id,
    action_ids: actionIds,
    rule_set_name: ruleSet.rule_set_name,
    rule_set_description: ruleSet.rule_set_description || undefined,
    rule_set_items: serializeRuleSetItemsForApi(items),
  }
}

/** Map string list (e.g. emails) to table rows for AgentItemsTable. */
export function stringsToItemRows(values, parameterPrefix = 'item') {
  const list = Array.isArray(values) ? values : []
  return list.map((text, index) => ({
    item_sequence: index + 1,
    parameter_name: `${parameterPrefix}_${index + 1}`,
    required: false,
    item_text: String(text ?? '').trim(),
  }))
}

export function itemRowsToStrings(items) {
  return serializeInformationItemsForApi(items, { includeParameterName: false })
    .map((row) => row.item_text)
    .filter(Boolean)
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i
const MOBILE_10_DIGIT_REGEX = /^[6-9]\d{9}$/

export function normalizeMobileDigits(value) {
  return String(value ?? '').replace(/\D/g, '').slice(-10)
}

export const DEFAULT_COUNTRY_DIAL_CODE = '+91'

export function formatContactNumberWithDialCode(
  mobile,
  dialCode = DEFAULT_COUNTRY_DIAL_CODE,
) {
  const digits = normalizeMobileDigits(mobile)
  const code = String(dialCode ?? DEFAULT_COUNTRY_DIAL_CODE).trim()
  return `${code}${digits}`
}

export function isValidEmail(value) {
  const email = String(value ?? '').trim()
  return email.length > 0 && EMAIL_REGEX.test(email)
}

export function isValidMobile10(value) {
  const digits = normalizeMobileDigits(value)
  return MOBILE_10_DIGIT_REGEX.test(digits)
}

export function itemRowsToMobileStrings(items) {
  return serializeInformationItemsForApi(items, { includeParameterName: false })
    .map((row) => normalizeMobileDigits(row.item_text))
    .filter((digits) => MOBILE_10_DIGIT_REGEX.test(digits))
}

export const emailFieldRules = [
  (v) => !!String(v ?? '').trim() || 'Email is required',
  (v) => isValidEmail(v) || 'Enter a valid email address',
]

export const mobileFieldRules = [
  (v) => !!String(v ?? '').trim() || 'Mobile number is required',
  (v) => isValidMobile10(v) || 'Enter a valid 10-digit mobile number',
]

export function normalizeOutboundAction(raw, index = 0) {
  const actionId =
    pickString(raw, 'action_id', 'actionId', 'id') || `action-${index}`
  const status = pickString(raw, 'action_status', 'actionStatus', 'status')
  let ruleSetId = pickString(raw, 'rule_set_id', 'ruleSetId')
  if (!ruleSetId) {
    const legacyIds = pickArray(raw, 'rule_set_ids', 'ruleSetIds')
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
    ruleSetId = legacyIds[0] || ''
  }

  return {
    id: actionId,
    action_id: actionId,
    action_name: pickString(raw, 'action_name', 'actionName', 'name'),
    action_prompt: pickString(raw, 'action_prompt', 'actionPrompt', 'prompt'),
    action_description: pickString(
      raw,
      'action_description',
      'actionDescription',
      'description',
    ),
    action_status: status ? status.toUpperCase() : 'ACTIVE',
    rule_set_id: ruleSetId,
    email_enabled: pickBool(raw, 'email_enabled', 'emailEnabled'),
    email_recipients: pickArray(raw, 'email_recipients', 'emailRecipients'),
    whatsapp_enabled: pickBool(raw, 'whatsapp_enabled', 'whatsappEnabled'),
    whatsapp_recipients: pickArray(
      raw,
      'whatsapp_recipients',
      'whatsappRecipients',
    ),
    webhook_enabled: pickBool(raw, 'webhook_enabled', 'webhookEnabled'),
    webhook_url: pickString(raw, 'webhook_url', 'webhookUrl'),
    webhook_method: pickString(raw, 'webhook_method', 'webhookMethod'),
  }
}

export function normalizeOutboundActionsList(rows) {
  const list = Array.isArray(rows) ? rows : []
  return list.map((row, index) => normalizeOutboundAction(row, index))
}

export function normalizeRuleSet(raw, index = 0) {
  const ruleSetId =
    pickString(raw, 'rule_set_id', 'ruleSetId', 'id') || `rule-set-${index}`

  let items = pickArray(raw, 'rule_set_items', 'ruleSetItems', 'items').map(
    (item, idx) => normalizeInformationInputItem(item, idx),
  )

  if (!items.length) {
    const information = pickArray(
      raw,
      'information',
      'info',
      'informationList',
    ).map((item, idx) =>
      normalizeInformationInputItem(
        { ...item, topic_type: 'INFORMATION' },
        idx,
      ),
    )
    const questions = pickArray(raw, 'questions', 'questionList').map(
      (item, idx) =>
        normalizeInformationInputItem({ ...item, topic_type: 'QUESTION' }, idx),
    )
    items = [...information, ...questions]
  }

  const actionIds = pickArray(raw, 'action_ids', 'actionIds')
    .map((id) => String(id ?? '').trim())
    .filter(Boolean)

  return {
    id: ruleSetId,
    rule_set_id: ruleSetId,
    action_ids: actionIds,
    rule_set_name: pickString(
      raw,
      'rule_set_name',
      'ruleSetName',
      'name',
    ),
    rule_set_description: pickString(
      raw,
      'rule_set_description',
      'ruleSetDescription',
      'description',
    ),
    items,
  }
}

export function normalizeRuleSetsList(rows) {
  const list = Array.isArray(rows) ? rows : []
  return list.map((row, index) => normalizeRuleSet(row, index))
}

export function ruleSetSelectOptions(ruleSets) {
  return (Array.isArray(ruleSets) ? ruleSets : [])
    .filter((row) => row.rule_set_id)
    .map((row) => ({
      title: row.rule_set_name || row.rule_set_id,
      value: row.rule_set_id,
    }))
}

export function summarizeRuleSetItems(items) {
  const rows = Array.isArray(items) ? items : []
  if (!rows.length) return '—'
  const infoCount = rows.filter(
    (row) => !isQuestionBlockType(row.topic_type),
  ).length
  const questionCount = rows.length - infoCount
  const parts = []
  if (infoCount) parts.push(`${infoCount} info`)
  if (questionCount) parts.push(`${questionCount} question${questionCount === 1 ? '' : 's'}`)
  return `${rows.length} item${rows.length === 1 ? '' : 's'} (${parts.join(', ')})`
}

/** Map action_id → rule_set_id from block action_ids lists. */
export function attachRuleSetsToActions(actions, ruleSets) {
  const actionToRuleSet = {}
  ;(Array.isArray(ruleSets) ? ruleSets : []).forEach((ruleSet) => {
    const ruleSetId = ruleSet.rule_set_id
    if (!ruleSetId) return
    ;(ruleSet.action_ids || []).forEach((actionId) => {
      const id = String(actionId ?? '').trim()
      if (!id || actionToRuleSet[id]) return
      actionToRuleSet[id] = ruleSetId
    })
  })

  return (Array.isArray(actions) ? actions : []).map((action) => {
    let ruleSetId = pickString(action, 'rule_set_id', 'ruleSetId')
    if (!ruleSetId) {
      const legacyIds = pickArray(action, 'rule_set_ids', 'ruleSetIds')
        .map((id) => String(id ?? '').trim())
        .filter(Boolean)
      ruleSetId = legacyIds[0] || ''
    }
    if (!ruleSetId) {
      ruleSetId = actionToRuleSet[action.action_id] || ''
    }
    return {
      ...action,
      rule_set_id: ruleSetId,
    }
  })
}
